import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';

import { useSaveIntegration } from 'src/api/queries';
import { Button, Form, Input, Modal } from 'src/components';

import { getProviderDisplayName } from '../utils/getProviderDisplayName';
import { providerIconByNameMap } from '../utils/iconMap';
import { useProviderCallback } from '../utils/useProviderCallback';
import { SaveIntegrationError } from './SaveIntegrationError';

interface SaveIntegrationForm {
  name: string;
}

const routeApi = getRouteApi('/_auth/_layout/integrations');

export const SaveIntegrationModal = () => {
  const { state, code, error } = routeApi.useSearch();

  // Each callback gets a fresh form and save result
  return <SaveIntegrationModalContent key={`${state}|${code}|${error}`} />;
};

const SaveIntegrationModalContent = () => {
  const { t } = useLingui();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const form = useForm<SaveIntegrationForm>();
  const { isCallbackUrl, data, error } = useProviderCallback();

  const saveNewIntegration = useSaveIntegration();

  const onSubmit = (formData: SaveIntegrationForm) => {
    if (!data?.provider) return;

    saveNewIntegration.mutate(
      {
        provider: data.provider,
        integration: {
          name: formData.name || getProviderDisplayName(data.provider),
          code: data.code,
          state: data.state,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          scope: data.scope ? data.scope : undefined,
        },
      },
      {
        onSuccess() {
          void navigate({ search: { callback: undefined } });
        },
      }
    );
  };

  const icon = data?.provider
    ? (providerIconByNameMap[data.provider] ?? null)
    : null;

  const displayError = error ?? saveNewIntegration.error;

  if (!isCallbackUrl) return null;

  return (
    <Modal.Root
      open={search.callback === true}
      onOpenChange={(isOpen) => {
        void navigate({ search: { callback: isOpen ? true : undefined } });
      }}
    >
      <Modal.Content>
        <div data-cy="save-integration-modal">
          {displayError ? (
            <SaveIntegrationError error={displayError} />
          ) : (
            <>
              <Typography variant="heading-sm" className="mb-32">
                <Trans context="save-integration-modal">
                  Name your service
                </Trans>
              </Typography>
              <p>
                <Trans context="save-integration-modal">
                  Your service was successfully connected
                </Trans>
              </p>
              <p className="my-16">
                <Trans context="save-integration-modal">
                  If you want to, you can give it a name, for example “Work”, to
                  easily tell the difference between the services you have
                  connected.
                </Trans>
              </p>
              <p>
                <Trans context="save-integration-modal">
                  You can set maximum 14 characters.
                </Trans>
              </p>
              <Form
                {...form}
                onSubmit={onSubmit}
                dataCy="save-integration-modal-form"
              >
                <div className="mt-32 flex items-start justify-center gap-16">
                  {icon !== null && (
                    <div className="flex h-48 items-center">
                      <img
                        src={icon}
                        alt={t`Integration provider`}
                        className="size-32"
                      />
                    </div>
                  )}
                  <Input
                    name="name"
                    placeholder={t({
                      message: 'Name',
                      context: 'save-integration-modal',
                      comment:
                        'Input placeholder for naming a connected integration',
                    })}
                    className="!my-0 max-w-[200px] text-left"
                    inputClassName="border border-gray-800"
                    maxLength={14}
                    data-cy="save-integration-name-input"
                  />
                  <Button
                    type="submit"
                    loading={saveNewIntegration.isPending}
                    className="h-48"
                    data-cy="integration-name-submit-button"
                  >
                    <Trans context="save-integration-modal">Save</Trans>
                  </Button>
                </div>
              </Form>
            </>
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
